import React, { useEffect, useMemo, useState } from "react";
import {
  slashAppErrorMessage,
  slashAppMonthly,
} from "../../utils/slashAppMonthly";

// 「レッスン一覧」タブの回の作成・編集。
// シフト調整の登録（直近の回をコピー）と違い、新しいクラスも作れるよう全項目を入力する。
// 書き込みは slash-app の管理者APIが行い、重なりの判定もそちらと同じ。
// 承認済みで作るのですぐホームページに掲載される。講師・申込者への連絡はしない。
// カード画像は未指定(NULL)ならスタジオページと同じ講師のメイン画像が出る。
// 変えたい回だけ画像をアップロードして差し替える。
// 開講最少人数は既定3名。申込（キャンセルを除く）がこの人数に達すると、
// 次の確認時刻（10時・14時・18時）に開講が決まる。

// 作成時の初期値。店舗は名前で探す（見つからなければ未選択のまま）。
const DEFAULT_STUDIO_NAME = "木津川";
const DEFAULTS = {
  capacity: "30",
  min_participants: "3",
  price: "3000",
  member_price: "2500",
};

const toMinutes = (hhmm) => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};

// 前の回の終わりと同時に始めるのは重なりとみなさない（サーバーと同じ判定）。
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && bStart < aEnd;

const isInt = (v) => /^\d+$/.test(String(v).trim());

// ホームページ。シフト調整で引き継いだ画像URLはサイト内のパス(/images/…)のことがある。
const SITE_ORIGIN = "https://dance-slash.com";
const NO_IMAGE = `${SITE_ORIGIN}/images/instructor_no-img_02.png`;
const siteUrl = (url) => (url && url.startsWith("/") ? `${SITE_ORIGIN}${url}` : url);

/**
 * サイトのマンスリーのカードの丸い写真と同じ見え方。スタジオページの
 * ヒーロー画像と同じく、中央で切り抜いて円を埋める（拡大・位置の調整はしない）。
 */
function CardImagePreview({ src }) {
  return (
    <div className="ml-card-clip">{src ? <img src={src} alt="" /> : null}</div>
  );
}

function initialForm(lesson) {
  if (!lesson) {
    return {
      name: "",
      studio_id: "",
      instructor_id: "",
      date: "",
      start_time: "",
      end_time: "",
      instructor_image_url: "",
      ...DEFAULTS,
    };
  }
  return {
    name: lesson.name,
    studio_id: String(lesson.studio_id),
    instructor_id: String(lesson.instructor_id),
    date: lesson.date,
    start_time: lesson.start_time,
    end_time: lesson.end_time,
    capacity: String(lesson.capacity),
    min_participants: String(lesson.min_participants ?? DEFAULTS.min_participants),
    price: String(lesson.price),
    member_price: lesson.member_price === null ? "" : String(lesson.member_price),
    instructor_image_url: lesson.instructor_image_url || "",
  };
}

/**
 * @param {object|null} lesson 編集する回（一覧APIの1件）。null なら新規作成。
 * @param {string} minDate 選べる最初の開催日（明日）"YYYY-MM-DD"
 * @param {string[]} classNames クラス名の入力候補（既存の回の名前）
 */
function MonthlyLessonFormModal({ lesson, minDate, classNames, onClose, onDone }) {
  const isEdit = !!lesson;
  const [form, setForm] = useState(() => initialForm(lesson));
  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsError, setSlotsError] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  // 未入力の指摘は、一度「登録」を押してから出す（開いた直後から赤くしない）。
  const [tried, setTried] = useState(false);
  // 講師の既定のカード画像（undefined: 読み込み中 / null: 取得できなかった）
  const [defaultImage, setDefaultImage] = useState(undefined);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(null);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    let alive = true;
    slashAppMonthly
      .lessonOptions()
      .then((data) => {
        if (!alive) return;
        setOptions(data);
        if (!isEdit) {
          const studio = data.studios.find((s) => s.name === DEFAULT_STUDIO_NAME);
          if (studio) {
            setForm((prev) =>
              prev.studio_id ? prev : { ...prev, studio_id: String(studio.id) },
            );
          }
        }
      })
      .catch((e) => {
        console.error(e);
        if (alive)
          setOptionsError(slashAppErrorMessage(e, "店舗・講師を取得できませんでした"));
      });
    return () => {
      alive = false;
    };
  }, [isEdit]);

  // 無効になった講師・閉じた店舗の回を編集するとき、選択肢に今の値が無いと
  // 空欄に見えるので、今の値だけ足しておく（変えなければそのまま保存できる）。
  const instructors = useMemo(() => {
    const list = options?.instructors || [];
    if (!isEdit || list.some((i) => i.id === lesson.instructor_id)) return list;
    return [
      {
        id: lesson.instructor_id,
        username: lesson.instructor_username || `#${lesson.instructor_id}`,
        dancer_name: lesson.instructor_name || "",
      },
      ...list,
    ];
  }, [options, isEdit, lesson]);

  const studios = useMemo(() => {
    const list = options?.studios || [];
    if (!isEdit || list.some((s) => s.id === lesson.studio_id)) return list;
    return [{ id: lesson.studio_id, name: lesson.studio_name || `#${lesson.studio_id}` }, ...list];
  }, [options, isEdit, lesson]);

  // その日に講師本人とスタジオが埋まっている時間帯。
  const { instructor_id: instructorId, studio_id: studioId, date } = form;

  // 画像を指定しないときにサイトに出る、講師のメイン画像。
  useEffect(() => {
    setDefaultImage(undefined);
    if (!instructorId) return undefined;
    let alive = true;
    const apiUrl = process.env.REACT_APP_API_URL;
    fetch(`${apiUrl}/api/get_monthly_instructor_image/?instructor_id=${instructorId}`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.json();
      })
      .then((data) => alive && setDefaultImage(data))
      .catch((e) => {
        console.error(e);
        if (alive) setDefaultImage(null);
      });
    return () => {
      alive = false;
    };
  }, [instructorId]);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    setImageError(null);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/upload_monthly_lesson_image/`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || "画像のアップロードに失敗しました");
      set({ instructor_image_url: data.url });
    } catch (e) {
      console.error(e);
      setImageError(e.message);
    } finally {
      setUploading(false);
    }
  };
  useEffect(() => {
    setSlots([]);
    setSlotsError(null);
    if (!instructorId || !studioId || !date || date < minDate) return undefined;
    let alive = true;
    slashAppMonthly
      .daySchedule(instructorId, studioId, date)
      .then((res) => {
        if (!alive) return;
        // 編集中の回そのものは除く（サーバーも自分自身とは重ならない扱い）。
        setSlots(
          res.slots.filter(
            (s) =>
              !(
                isEdit &&
                date === lesson.date &&
                s.kind === "monthly" &&
                s.studio_id === lesson.studio_id &&
                s.name === lesson.name &&
                s.start_time === lesson.start_time &&
                s.end_time === lesson.end_time
              ),
          ),
        );
      })
      .catch((e) => {
        console.error(e);
        if (alive)
          setSlotsError(slashAppErrorMessage(e, "この日の予定を取得できませんでした"));
      });
    return () => {
      alive = false;
    };
  }, [instructorId, studioId, date, minDate, isEdit, lesson]);

  // 入力の検査（サーバーと同じ条件）。未入力(missing)と誤り(errors)を分け、
  // 申込者に関わる変更は注意（黄）で出す。
  const missing = [];
  const errors = [];
  const warnings = [];
  const start = toMinutes(form.start_time);
  const end = toMinutes(form.end_time);
  if (!form.name.trim()) missing.push("クラス名を入力してください");
  if (!form.studio_id) missing.push("店舗を選択してください");
  if (!form.instructor_id) missing.push("講師を選択してください");
  if (!form.date) {
    missing.push("開催日を選択してください");
  } else if (form.date < minDate) {
    errors.push("開催日は明日以降の日付を選択してください");
  }
  if (start === null || end === null) {
    missing.push("開始・終了時刻を入力してください");
  } else if (start >= end) {
    errors.push("終了時刻は開始時刻より後にしてください");
  } else {
    slots.forEach((s) => {
      if (overlaps(start, end, toMinutes(s.start_time), toMinutes(s.end_time))) {
        errors.push(
          `${s.start_time}–${s.end_time}「${s.name}」（${
            s.is_own ? "講師本人" : s.instructor_name
          }・${s.studio_name}）と重なっています`,
        );
      }
    });
  }
  const applied = isEdit ? lesson.applied_count : 0;
  if (!isInt(form.capacity) || parseInt(form.capacity, 10) < 1) {
    errors.push("定員は1以上にしてください");
  } else if (parseInt(form.capacity, 10) < applied) {
    errors.push(`定員は申込数（${applied}名）以上にしてください`);
  }
  const minParticipants = parseInt(form.min_participants, 10);
  if (!isInt(form.min_participants) || minParticipants < 1) {
    errors.push("開講最少人数は1以上にしてください");
  } else if (isInt(form.capacity) && minParticipants > parseInt(form.capacity, 10)) {
    errors.push("開講最少人数は定員以下にしてください");
  }
  if (!isInt(form.price)) errors.push("料金を入力してください");
  if (form.member_price !== "") {
    if (!isInt(form.member_price)) {
      errors.push("会員価格を正しく入力してください");
    } else if (isInt(form.price) && parseInt(form.member_price, 10) > parseInt(form.price, 10)) {
      errors.push("会員価格は料金以下にしてください");
    }
  }
  if (applied > 0) {
    const initial = initialForm(lesson);
    const changed = (keys) => keys.some((k) => form[k] !== initial[k]);
    if (changed(["date", "start_time", "end_time", "studio_id"])) {
      warnings.push(
        `申込者${applied}名には自動で連絡されません。日時・店舗の変更は運営から連絡してください`,
      );
    }
    if (changed(["price", "member_price"])) {
      warnings.push(`申込済みの${applied}名は、申込時の金額のまま請求されます`);
    }
    if (changed(["min_participants"]) && minParticipants >= 1 && applied >= minParticipants) {
      warnings.push(
        `申込${applied}名が開講最少人数に達するので、次の確認時刻（10時・14時・18時）に開講が決まり、申込者と講師に連絡が届きます`,
      );
    }
  }
  const hasError = missing.length > 0 || errors.length > 0;
  const shownErrors = tried ? [...missing, ...errors] : errors;

  const submit = async () => {
    if (sending || uploading || !options) return;
    setTried(true);
    if (hasError) return;
    if (
      warnings.length > 0 &&
      !window.confirm(`${warnings.join("。\n")}。\n\nこのまま保存しますか？`)
    ) {
      return;
    }
    setSending(true);
    setError(null);
    const body = {
      name: form.name.trim(),
      studio_id: parseInt(form.studio_id, 10),
      instructor_id: parseInt(form.instructor_id, 10),
      date: form.date,
      start_time: form.start_time,
      end_time: form.end_time,
      capacity: parseInt(form.capacity, 10),
      min_participants: parseInt(form.min_participants, 10),
      price: parseInt(form.price, 10),
      member_price: form.member_price === "" ? null : parseInt(form.member_price, 10),
      // 空なら講師のメイン画像（NULL）に戻す
      instructor_image_url: form.instructor_image_url || null,
    };
    try {
      const saved = isEdit
        ? await slashAppMonthly.updateLesson(lesson.id, body)
        : await slashAppMonthly.createLesson(body);
      await onDone(saved);
    } catch (e) {
      console.error(e);
      setError(slashAppErrorMessage(e, isEdit ? "保存に失敗しました" : "登録に失敗しました"));
      setSending(false);
    }
  };

  const close = () => {
    if (!sending) onClose();
  };

  return (
    <div
      className="review-overlay"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div className="review-modal sa-modal ml-form-modal" role="dialog" aria-modal="true">
        <div className="review-modal-head">
          <h3>{isEdit ? "マンスリーレッスンを編集" : "マンスリーレッスンを追加"}</h3>
          <button type="button" className="review-x" onClick={close} aria-label="閉じる">
            ×
          </button>
        </div>
        <div className="review-modal-body">
          {optionsError ? <p className="sa-error-text">{optionsError}</p> : null}

          <div className="ml-form">
            <label className="ml-field is-wide">
              <span className="review-label">クラス名</span>
              <input
                type="text"
                list="ml-class-names"
                maxLength={255}
                value={form.name}
                placeholder="例）GIRLS HIPHOP"
                onChange={(e) => set({ name: e.target.value })}
              />
              <datalist id="ml-class-names">
                {classNames.map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
            </label>

            <label className="ml-field">
              <span className="review-label">店舗</span>
              <select
                value={form.studio_id}
                onChange={(e) => set({ studio_id: e.target.value })}
                disabled={!options}
              >
                <option value="">{options ? "選択してください" : "読み込み中…"}</option>
                {studios.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="ml-field">
              <span className="review-label">講師</span>
              <select
                value={form.instructor_id}
                onChange={(e) => set({ instructor_id: e.target.value })}
                disabled={!options}
              >
                <option value="">{options ? "選択してください" : "読み込み中…"}</option>
                {instructors.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.dancer_name && i.dancer_name !== i.username
                      ? `${i.username}（${i.dancer_name}）`
                      : i.username}
                  </option>
                ))}
              </select>
            </label>

            <label className="ml-field">
              <span className="review-label">開催日</span>
              <input
                type="date"
                min={minDate}
                value={form.date}
                onChange={(e) => set({ date: e.target.value })}
              />
            </label>

            <div className="ml-field">
              <span className="review-label">時間</span>
              <div className="ml-time-range">
                <input
                  type="time"
                  step={300}
                  value={form.start_time}
                  aria-label="開始時刻"
                  onChange={(e) => set({ start_time: e.target.value })}
                />
                <span>〜</span>
                <input
                  type="time"
                  step={300}
                  value={form.end_time}
                  aria-label="終了時刻"
                  onChange={(e) => set({ end_time: e.target.value })}
                />
              </div>
            </div>

            <label className="ml-field">
              <span className="review-label">定員</span>
              <input
                type="number"
                min={1}
                value={form.capacity}
                onChange={(e) => set({ capacity: e.target.value })}
              />
            </label>

            <label className="ml-field">
              <span className="review-label">
                開講最少人数<span className="optional">この人数で開講</span>
              </span>
              <input
                type="number"
                min={1}
                value={form.min_participants}
                onChange={(e) => set({ min_participants: e.target.value })}
              />
            </label>

            <label className="ml-field">
              <span className="review-label">料金（税込）</span>
              <input
                type="number"
                min={0}
                step={100}
                value={form.price}
                onChange={(e) => set({ price: e.target.value })}
              />
            </label>

            <label className="ml-field">
              <span className="review-label">
                会員価格（税込）<span className="optional">空欄でなし</span>
              </span>
              <input
                type="number"
                min={0}
                step={100}
                value={form.member_price}
                placeholder="なし"
                onChange={(e) => set({ member_price: e.target.value })}
              />
            </label>

            <div className="ml-field is-wide">
              <span className="review-label">
                カード画像
                <span className="optional">
                  {form.instructor_image_url ? "この回だけの画像" : "講師のメイン画像"}
                </span>
              </span>
              <div className="ml-card-image">
                {form.instructor_image_url ? (
                  <CardImagePreview src={siteUrl(form.instructor_image_url)} />
                ) : defaultImage ? (
                  <CardImagePreview src={defaultImage.url || NO_IMAGE} />
                ) : (
                  <CardImagePreview src={null} />
                )}
                <div className="ml-card-image-side">
                  <p className="review-note">
                    {form.instructor_image_url
                      ? "アップロードした画像をこの回のカードに出します（中央で丸く切り抜きます）。"
                      : !form.instructor_id
                        ? "講師を選ぶと、カードに出る画像が表示されます。"
                        : defaultImage === undefined
                          ? "読み込み中…"
                          : defaultImage === null
                            ? "講師の画像を取得できませんでした。"
                            : defaultImage.url
                              ? "スタジオページのメイン画像を出します。"
                              : "スタジオページのメイン画像が未設定のため「画像なし」が出ます。"}
                  </p>
                  <div className="ml-card-image-actions">
                    <label className={`act act-ghost ${uploading ? "is-disabled" : ""}`}>
                      {uploading ? "アップロード中…" : "画像をアップロード"}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        disabled={uploading || sending}
                        onChange={(e) => {
                          uploadImage(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {form.instructor_image_url ? (
                      <button
                        type="button"
                        className="act act-ghost"
                        disabled={uploading || sending}
                        onClick={() => set({ instructor_image_url: "" })}
                      >
                        メイン画像に戻す
                      </button>
                    ) : null}
                  </div>
                  {imageError ? <p className="sa-error-text">{imageError}</p> : null}
                </div>
              </div>
            </div>
          </div>

          {form.instructor_id && form.studio_id && form.date >= minDate ? (
            <div className="sa-slots">
              <div className="review-label">この日の予定（講師本人・同じ店舗）</div>
              {slotsError ? (
                <p className="sa-error-text">{slotsError}</p>
              ) : slots.length === 0 ? (
                <p className="review-note">埋まっている時間帯はありません</p>
              ) : (
                <ul>
                  {slots.map((s, i) => (
                    <li key={`${s.kind}-${s.studio_id}-${s.start_time}-${i}`}>
                      <span className="sa-slot-time">
                        {s.start_time}–{s.end_time}
                      </span>
                      {s.kind === "monthly" ? "マンスリー" : s.kind === "makeup" ? "振替" : "通常"}
                      「{s.name}」 {s.is_own ? "講師本人" : s.instructor_name}・{s.studio_name}
                      {s.canceled ? "（休講日）" : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {shownErrors.map((m) => (
            <p key={m} className="sa-error-text">
              {m}
            </p>
          ))}
          {warnings.map((m) => (
            <p key={m} className="sa-warn-text">
              {m}
            </p>
          ))}

          <p className="review-note">
            {isEdit
              ? "保存した内容はすぐにホームページに反映されます。"
              : "登録するとすぐにホームページに掲載され、予約の受付が始まります。"}
          </p>
          {error ? <p className="sa-error-text">{error}</p> : null}
        </div>
        <div className="review-modal-foot">
          <button type="button" className="act act-ghost" onClick={close} disabled={sending}>
            キャンセル
          </button>
          <button
            type="button"
            className="act act-go"
            onClick={submit}
            disabled={sending || uploading || !options || (tried && hasError)}
          >
            {sending ? "保存中…" : isEdit ? "保存する" : "登録して掲載"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MonthlyLessonFormModal;
